import { z } from 'zod'

/** Max digits for invite income before decimal (999999.99). */
export const INCOME_MAX_DIGITS_BEFORE_DECIMAL = 6

/** Max digits for invite income after decimal. */
export const INCOME_MAX_DIGITS_AFTER_DECIMAL = 2

export const INCOME_DIGIT_LIMIT_MESSAGE = `Income must have at most ${INCOME_MAX_DIGITS_BEFORE_DECIMAL} digits before the decimal and ${INCOME_MAX_DIGITS_AFTER_DECIMAL} after the decimal.`

/** Rent / mortgage caps — independent of invite income limits. */
export const RENT_AMOUNT_MAX_DIGITS_BEFORE_DECIMAL = 15

/** Returns a validation message when income exceeds 6+2 digit limits; otherwise undefined. */
export function getIncomeDigitLimitValidationMessage(
  income: string | undefined | null,
): string | undefined {
  if (!income || !String(income).trim()) return undefined
  const raw = String(income).trim().replace(/[^0-9.]/g, '')
  const [intPart = '', decPart = ''] = raw.split('.')
  const before = intPart.replace(/\D/g, '')
  const after = decPart.replace(/\D/g, '')
  if (
    before.length > INCOME_MAX_DIGITS_BEFORE_DECIMAL ||
    after.length > INCOME_MAX_DIGITS_AFTER_DECIMAL
  ) {
    return INCOME_DIGIT_LIMIT_MESSAGE
  }
  return undefined
}

/** True when income is empty or within 6 digits before and 2 after the decimal. */
export function incomeWithinDigitLimits(income: string | undefined | null): boolean {
  return getIncomeDigitLimitValidationMessage(income) === undefined
}

type IncomeFieldRefineData = { income?: string }

/** Shared Zod refine for invite primary/co-applicant income digit limits. */
export const incomeDigitLimitSchemaRefine = {
  check: (data: IncomeFieldRefineData) => incomeWithinDigitLimits(data.income),
  config: {
    message: INCOME_DIGIT_LIMIT_MESSAGE,
    path: ['income'],
  },
}

export const RENT_AMOUNT_MAX_DIGITS_MESSAGE = `Rent amount cannot have more than ${RENT_AMOUNT_MAX_DIGITS_BEFORE_DECIMAL} digits before the decimal.`

export const AMOUNT_MAX_DIGITS_MESSAGE = `Amount cannot have more than ${RENT_AMOUNT_MAX_DIGITS_BEFORE_DECIMAL} digits before the decimal.`

export const MORTGAGE_AMOUNT_INVALID_MESSAGE = 'Enter a valid amount.'

/** Integer part length after stripping non-numeric (matches CurrencyInput behavior). */
export function rentAmountExceedsMaxDigits(value: string | undefined | null): boolean {
  const raw = String(value ?? '')
    .trim()
    .replace(/[^0-9.]/g, '')
  if (!raw) return false
  const intPart = raw.split('.')[0] ?? ''
  return intPart.length > RENT_AMOUNT_MAX_DIGITS_BEFORE_DECIMAL
}

/** Edit/add co-applicant dialog — matches invitee API wording. */
export const CO_APPLICANT_EMAIL_UNIQUE_MESSAGE =
  'Co-applicant Email should be different from Primary Applicant / Co-applicant.'

export const CO_APPLICANT_PHONE_UNIQUE_MESSAGE =
  'Co-applicant Phone Number should be different from Primary Applicant / Co-applicant.'

/**
* Name validation pattern from API documentation
* Pattern: /^(?! )[a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:\'",.<>?/`~\\-]+(?: [a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:\'",.<>?/`~\\-]+){0,2}(?<! )$/
* Cannot start or end with space, max 3 words
*/
export const namePattern = /^(?! )[a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:'",.<>?/`~\\-]+(?: [a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:'",.<>?/`~\\-]+){0,2}(?<! )$/

/**
* Mobile phone validation pattern (matches Angular's mobileValidator)
* Pattern: ^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$
* This matches:
* - 10 digits: (XXX) XXX-XXXX (no country code)
* - 11 digits: +X (XXX) XXX-XXXX (1-digit country code like US +1)
* - 12 digits: +XX (XXX) XXX-XXXX (2-digit country code)
*/
export const mobilePattern = /^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/

/** Single message for invalid mobile — matches EditInviteeDialog blur + Angular wording. */
export const MOBILE_PHONE_PATTERN_MESSAGE =
  'Enter Valid Contact Phone, Should match pattern (XXX-XXX-XXXX).'

/** Invite primary/co-applicant email format (TLD min 3 chars). */
export const INVITEE_EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{3,}$/

export const INVITEE_EMAIL_REQUIRED_MESSAGE = 'Email is required.'
export const INVITEE_EMAIL_INVALID_MESSAGE = 'Email is not valid.'

export const INVITE_FIRST_NAME_REQUIRED_MESSAGE = 'First Name is required.'
export const INVITE_LAST_NAME_REQUIRED_MESSAGE = 'Last Name is required.'
export const INVITE_FIRST_NAME_INVALID_MESSAGE = 'Enter Valid First Name'
export const INVITE_LAST_NAME_INVALID_MESSAGE = 'Enter Valid Last Name.'

/** Returns a field-level email error, or undefined when valid. */
export function getInviteEmailFieldError(value: unknown): string | undefined {
  if (isBlankInviteField(value)) {
    return INVITEE_EMAIL_REQUIRED_MESSAGE
  }
  if (!INVITEE_EMAIL_PATTERN.test(String(value).trim())) {
    return INVITEE_EMAIL_INVALID_MESSAGE
  }
  return undefined
}

/** True when value is non-empty but fails {@link INVITEE_EMAIL_PATTERN}. */
export function isInviteEmailFormatInvalid(value: unknown): boolean {
  const email = String(value ?? '').trim()
  return email.length > 0 && !INVITEE_EMAIL_PATTERN.test(email)
}

/** Returns a field-level first name error, or undefined when valid. */
export function getInviteFirstNameFieldError(value: unknown): string | undefined {
  if (isBlankInviteField(value)) {
    return INVITE_FIRST_NAME_REQUIRED_MESSAGE
  }
  if (!namePattern.test(String(value))) {
    return INVITE_FIRST_NAME_INVALID_MESSAGE
  }
  return undefined
}

/** Returns a field-level last name error, or undefined when valid. */
export function getInviteLastNameFieldError(value: unknown): string | undefined {
  if (isBlankInviteField(value)) {
    return INVITE_LAST_NAME_REQUIRED_MESSAGE
  }
  if (!namePattern.test(String(value))) {
    return INVITE_LAST_NAME_INVALID_MESSAGE
  }
  return undefined
}

/**
* Validate Mobile Number using the same regex as Angular
* Strips non-numeric characters first, then validates against the pattern
*/
export function validateMobile(value: string): boolean {
  if (!value) return false
  // Strip all non-numeric characters first (like Angular's validateData does)
  const digits = value.replace(/\D/g, '')
  // Use the same regex pattern as Angular's mobileValidator
  // This accepts 10, 11, or 12 digits
  const match = digits.match(/^(\d{1,2})?(\d{3})(\d{3})(\d{4})$/)
  return !!match
}

/**
* Validate future date
* Date must be in the future
*/
export function validateFutureDate(dateString: string): boolean {
  if (!dateString) return false
  const date = new Date(dateString)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date > today
}

/** Minimum accepted monthly rent (inclusive), matches API: error when rent_amount is less than 10. */
export const RENT_AMOUNT_MIN_VALUE = 10

/**
 * Parse a formatted rent string ($, commas) to a number. Non-finite → NaN.
 */
export function parseRentAmountNumeric(value: string): number {
  if (value == null) return NaN
  const raw = String(value).trim().replace(/[^0-9.-]/g, '')
  if (raw === '' || raw === '-' || raw === '.') return NaN
  const n = parseFloat(raw)
  return Number.isFinite(n) ? n : NaN
}

/** Empty rent — inline under field on Apply / Send; no min-$10 modal. */
export const RENT_AMOUNT_REQUIRED_MESSAGE = 'Rent amount is required.'

/** Unparseable rent (submit). */
export const RENT_AMOUNT_INVALID_MESSAGE = 'Enter a valid rent amount.'

/**
 * Rent strictly below {@link RENT_AMOUNT_MIN_VALUE} — center modal on Apply / Send only (not on blur).
 */
export const RENT_AMOUNT_MIN_SUBMIT_MESSAGE = 'Rent must be at least $10.'
const INCOME_VERIFICATION_REQUIRED_MESSAGE = 'Please select Income Verification.'

/**
 * Rent min / required check for Add Invitee — run only on Send Invitation / Apply Now (not in Zod).
 */
export function validateRentAmountForSubmit(value: string | undefined): string | null {
  if (value == null || String(value).trim() === '') {
    return RENT_AMOUNT_REQUIRED_MESSAGE
  }
  const n = parseRentAmountNumeric(value)
  if (Number.isNaN(n)) {
    return RENT_AMOUNT_INVALID_MESSAGE
  }
  if (n < RENT_AMOUNT_MIN_VALUE) {
    return RENT_AMOUNT_MIN_SUBMIT_MESSAGE
  }
  return null
}

/**
 * Zod: required (trimmed non-empty) so rent surfaces on the same submit pass as other fields.
 * Minimum $10 (inclusive) and invalid numeric input: {@link validateRentAmountForSubmit} after Zod passes.
 */
export const rentAmountFormFieldSchema = z
  .string()
  .refine((val) => String(val ?? '').trim().length > 0, {
    message: RENT_AMOUNT_REQUIRED_MESSAGE,
  })

/** Trim-empty check shared by invite name/email/mobile fields (required before format). */
function isBlankInviteField(value: unknown): boolean {
  return value == null || String(value).trim() === ''
}

/**
 * First/last name: required when empty/whitespace; pattern only when a value is present.
 * Matches Angular invite-add (required before pattern) and EditInviteeDialog.
 */
function createInviteNameSchema(requiredMessage: string, invalidMessage: string) {
  return z.string().superRefine((val, ctx) => {
    if (isBlankInviteField(val)) {
      ctx.addIssue({ code: 'custom', message: requiredMessage })
      return
    }
    if (!namePattern.test(String(val))) {
      ctx.addIssue({ code: 'custom', message: invalidMessage })
    }
  })
}

/**
* Base name schema
*/
export const nameSchema = createInviteNameSchema(
  'First Name is required.',
  'Enter Valid First Name',
)

/**
* Last name schema
*/
export const lastNameSchema = createInviteNameSchema(
  'Last Name is required.',
  'Enter Valid Last Name.',
)

/**
* Mobile phone schema
*/
export const mobileSchema = z.string().superRefine((val, ctx) => {
  if (isBlankInviteField(val)) {
    ctx.addIssue({ code: 'custom', message: 'Mobile Number is required.' })
    return
  }
  if (!validateMobile(String(val))) {
    ctx.addIssue({ code: 'custom', message: MOBILE_PHONE_PATTERN_MESSAGE })
  }
})

/**
* Email schema
*/
export const emailSchema = z.string().superRefine((val, ctx) => {
  const message = getInviteEmailFieldError(val)
  if (message) {
    ctx.addIssue({ code: 'custom', message })
  }
})

/**
* Optional email schema
*/
export const optionalEmailSchema = z
  .string()
  .optional()
  .refine((val) => !val || z.string().email().safeParse(val).success, {
    message: 'Email is not valid.',
  })

/**
* Property confirmations schema
*/
export const propertyConfirmationsSchema = z.object({
  crime: z.enum(['0', '1'], { message: 'Selection is required.' }),
  approved_by_liberty: z.enum(['0', '1'], { message: 'Selection is required.' }),
  allow_to_movein: z.enum(['0', '1'], { message: 'Selection is required.' }),
})

/**
* Primary applicant schema
*/
export const primaryApplicantSchema = z
  .object({
    first_name: nameSchema,
    last_name: lastNameSchema,
    email: emailSchema,
    mobile_no: mobileSchema,
    property_id: z.string().min(1, 'Property Selection is required.'),
    unit_number: z.string().max(100, 'Unit Number is Too Large (Max Length 100).').optional(),
    rent_amount: rentAmountFormFieldSchema,
    lease_start_date: z
      .string()
      .min(1, 'Lease Start Date is required.')
      .refine(validateFutureDate, {
        message: 'The Lease Start Date needs to be in the Future.',
      }),
    lease_duration: z.string(),
    income_verified: z
      .union([z.enum(['0', '1']), z.literal(''), z.undefined()])
      .refine((v) => v === '0' || v === '1', {
        message: INCOME_VERIFICATION_REQUIRED_MESSAGE,
      }),
    income: z.string().optional(),
    income_period: z.string(),
  })
  .refine(
    (data) => {
      // If income_verified === '1', income must be provided (Angular: incomeValidator)
      if (data.income_verified === '1') {
        return !!data.income && data.income.trim().length > 0
      }
      return true
    },
    {
      message: 'Please Enter Income Total.',
      path: ['income'],
    }
  )
  .refine(incomeDigitLimitSchemaRefine.check, incomeDigitLimitSchemaRefine.config)

/**
* Co-applicant schema
*/
export const coApplicantSchema = z
  .object({
    first_name: nameSchema,
    last_name: lastNameSchema,
    email: emailSchema,
    mobile_no: mobileSchema,
    relationship_type: z.enum(
      ['SPOUSE', 'SIBLING', 'CHILD', 'PARENT', 'NOT_DIRECTLY_RELATED', 'CO_SIGNER'],
      { message: 'Relationship is required.' }
    ),
    living_unit: z.enum(['0', '1'], { message: 'Please select an option for the above question.' }),
    is_approved_require: z.enum(['0', '1'], { message: 'Liberty Approval Needed Selection is required.' }),
    income_verified: z
      .union([z.enum(['0', '1']), z.literal(''), z.undefined()])
      .refine((v) => v === '0' || v === '1', {
        message: INCOME_VERIFICATION_REQUIRED_MESSAGE,
      }),
    income: z.string().optional(),
  })
  .refine(
    (data) => {
      // If income_verified === '1', income must be provided
      if (data.income_verified === '1') {
        return !!data.income && data.income.trim().length > 0
      }
      return true
    },
    {
      message: 'Please Enter Income Total.',
      path: ['income'],
    }
  )
  .refine(
    (data) => {
      // If is_approved_require === '0', income_verified must be '1' (only verified income is allowed).
      if (data.is_approved_require === '0') {
        return data.income_verified === '1'
      }
      return true
    },
    {
      message: 'Income must be verified when Liberty Approval Needed is No.',
      path: ['income_verified'],
    }
  )
  .refine(
    (data) => {
      // If is_approved_require === '0', income must be provided
      if (data.is_approved_require === '0') {
        return !!data.income && data.income.trim().length > 0
      }
      return true
    },
    {
      message: 'Please Enter Income Total.',
      path: ['income'],
    }
  )
  .refine(incomeDigitLimitSchemaRefine.check, incomeDigitLimitSchemaRefine.config)

/**
 * Add Invitee — client checks only (formats, required fields, income when verified, coApplicantSchema per row).
 * Duplicate email/phone across primary vs co-applicants is enforced by invite create API (see InviteeAdd 400/422 modal).
 */
export const inviteeFormSchema = z
  .object({
    // Property Confirmations
    crime: z.enum(['0', '1'], { message: 'Selection is required.' }),
    approved_by_liberty: z.enum(['0', '1'], { message: 'Selection is required.' }),
    allow_to_movein: z.enum(['0', '1'], { message: 'Selection is required.' }),

    // Primary Applicant
    first_name: nameSchema,
    last_name: lastNameSchema,
    email: emailSchema,
    mobile_no: mobileSchema,
    property_id: z.string().min(1, 'Property Selection is required.'),
    unit_number: z.string().max(100, 'Unit Number is Too Large (Max Length 100).').optional(),
    rent_amount: rentAmountFormFieldSchema,
    lease_start_date: z
      .string()
      .min(1, 'Lease Start Date is required.')
      .refine(validateFutureDate, {
        message: 'The Lease Start Date needs to be in the Future.',
      }),
    lease_duration: z.string(),
    income_verified: z
      .union([z.enum(['0', '1']), z.literal(''), z.undefined()])
      .refine((v) => v === '0' || v === '1', {
        message: INCOME_VERIFICATION_REQUIRED_MESSAGE,
      }),
    income: z.string().optional(),
    income_period: z.string(),

    // Co-Applicants
    co_applicant: z.array(coApplicantSchema),

    // Optional
    popid: z.string().optional(),
  })
  .refine(
    (data) => {
      // If income_verified === '1', income must be provided (Angular: incomeValidator → requiredIncome)
      if (data.income_verified === '1') {
        return !!data.income && data.income.trim().length > 0
      }
      return true
    },
    {
      message: 'Please Enter Income Total.',
      path: ['income'],
    }
  )
  .refine(incomeDigitLimitSchemaRefine.check, incomeDigitLimitSchemaRefine.config)

/**
* Validate co-applicant email is different from primary applicant email
*/
export function validateEmailDiff(
  coApplicantEmail: string,
  primaryEmail: string
): boolean {
  if (!coApplicantEmail || !primaryEmail) return true
  return coApplicantEmail.toLowerCase() !== primaryEmail.toLowerCase()
}

/**
* Validate co-applicant mobile is different from primary applicant mobile
*/
export function validateMobileDiff(
  coApplicantMobile: string,
  primaryMobile: string
): boolean {
  if (!coApplicantMobile || !primaryMobile) return true
  const coDigits = coApplicantMobile.replace(/\D/g, '')
  const primaryDigits = primaryMobile.replace(/\D/g, '')
  return coDigits !== primaryDigits
}

/**
* Type for invitee form data
*/
export type InviteeFormSchemaType = z.infer<typeof inviteeFormSchema>

/**
 * Edit invitee schema - used for editing existing invitees
 * Matches Angular's report-invite-edit.component.ts validations
 */
export const editInviteeSchema = z
  .object({
    first_name: nameSchema,
    last_name: lastNameSchema,
    email: emailSchema,
    mobile_no: mobileSchema,
    relationship_type: z.string().optional(),
    living_unit: z.string().optional(),
    is_approved_require: z.string().optional(),
    income_verified: z
      .union([z.enum(['0', '1']), z.literal(''), z.undefined()])
      .refine((v) => v === '0' || v === '1', {
        message: INCOME_VERIFICATION_REQUIRED_MESSAGE,
      }),
    income: z.string().optional(),
  })
  .refine(
    (data) => {
      // If income_verified === '1', income must be provided
      if (data.income_verified === '1') {
        const incomeValue = data.income?.replace(/[^0-9.]/g, '') || ''
        return incomeValue.length > 0 && parseFloat(incomeValue) > 0
      }
      return true
    },
    {
      message: 'Please Enter Income Total.',
      path: ['income'],
    }
  )
  .refine(
    (data) => {
      // If is_approved_require === '0' (No), income must be provided (underwritingRequireValidator)
      if (data.is_approved_require === '0') {
        const incomeValue = data.income?.replace(/[^0-9.]/g, '') || ''
        return incomeValue.length > 0 && parseFloat(incomeValue) > 0
      }
      return true
    },
    {
      message: 'Please Enter Income Total.',
      path: ['income'],
    }
  )
  .refine(
    (data) => {
      // If is_approved_require === '0', income_verified must be '1'
      if (data.is_approved_require === '0') {
        return data.income_verified === '1'
      }
      return true
    },
    {
      message: 'Income must be verified when Liberty Approval Needed is No.',
      path: ['income_verified'],
    }
  )
  .refine(incomeDigitLimitSchemaRefine.check, incomeDigitLimitSchemaRefine.config)

/**
 * Edit invitee schema with co-applicant fields required
 * Used when editing a co-applicant or adding a new co-applicant.
 * Email and mobile are both required (same as Add Invitee co-applicant section and form labels).
 * living_unit and is_approved_require default empty (no "Yes" pre-selected) – issue #1
 */
export const editCoApplicantSchema = z
  .object({
    first_name: nameSchema,
    last_name: lastNameSchema,
    email: emailSchema,
    mobile_no: mobileSchema,
    relationship_type: z.enum(
      ['SPOUSE', 'SIBLING', 'CHILD', 'PARENT', 'NOT_DIRECTLY_RELATED', 'CO_SIGNER'],
      { message: 'Relationship is required.' }
    ),
    living_unit: z.union([z.enum(['0', '1']), z.literal('')]).refine((v) => v === '0' || v === '1', { message: 'Please select an option for the above question.' }),
    is_approved_require: z.union([z.enum(['0', '1']), z.literal('')]).refine((v) => v === '0' || v === '1', { message: 'Liberty Approval Needed Selection is required.' }),
    income_verified: z
      .union([z.enum(['0', '1']), z.literal(''), z.undefined()])
      .refine((v) => v === '0' || v === '1', {
        message: INCOME_VERIFICATION_REQUIRED_MESSAGE,
      }),
    income: z.string().optional(),
  })
  .refine(
    (data) => {
      // If income_verified === '1', income must be provided
      if (data.income_verified === '1') {
        const incomeValue = data.income?.replace(/[^0-9.]/g, '') || ''
        return incomeValue.length > 0 && parseFloat(incomeValue) > 0
      }
      return true
    },
    {
      message: 'Please Enter Income Total.',
      path: ['income'],
    }
  )
  .refine(
    (data) => {
      // If is_approved_require === '0' (No), income must be provided (underwritingRequireValidator)
      if (data.is_approved_require === '0') {
        const incomeValue = data.income?.replace(/[^0-9.]/g, '') || ''
        return incomeValue.length > 0 && parseFloat(incomeValue) > 0
      }
      return true
    },
    {
      message: 'Please Enter Income Total.',
      path: ['income'],
    }
  )
  .refine(
    (data) => {
      // If is_approved_require === '0', income_verified must be '1'
      if (data.is_approved_require === '0') {
        return data.income_verified === '1'
      }
      return true
    },
    {
      message: 'Income must be verified when Liberty Approval Needed is No.',
      path: ['income_verified'],
    }
  )
  .refine(incomeDigitLimitSchemaRefine.check, incomeDigitLimitSchemaRefine.config)

/**
 * Type for edit invitee form data
 */
export type EditInviteeSchemaType = z.infer<typeof editInviteeSchema>
export type EditCoApplicantSchemaType = z.infer<typeof editCoApplicantSchema>
